import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { useAccountCreationContext } from '@/components/signup/account-creation/hooks/account-creation-context';
import { emailIsValid } from '@/components/signup/utils/email-is-valid';
import { fontSize } from '@/components/signup/utils/font-size';
import { wacToken } from '@/libraries/design/foundations';

import { InlineInputV2 } from './inline-input-v2';
import { InlineSeparator } from './inline-separator';
import { InlineSubmit } from './inline-submit';
import { SocialButton } from './social-button';

/** Atlaskit `media.above.md` — horizontal desktop layout at 1024px+ */
const DESKTOP_MQ = '(min-width: 64rem)';

type FormFieldsProps = {
	email: string;
	setEmail: Dispatch<SetStateAction<string>>;
	isValid: boolean | undefined;
	errorMessageText: string;
};

/**
 * Homepage Simplified form V2 (TWCG-2414).
 * Dual trees (no layout media queries) so Compiled can't half-apply desktop styles:
 * - stacked (&lt; md / tablet+mobile): email → Sign up → or → Google
 * - desktop (md+): grey outer pill, white nested field + or + Google
 * @see https://www.figma.com/design/KhAdeSJF2y30N2lm8qmRmi/Homepage-JIra-Signup?node-id=105-3759
 */
export const InlineInputSubmitSocialV2 = () => {
	const intl = useIntl();
	const { isReadyForInvalid, setIsEmailValid, typeAheadEmail } = useAccountCreationContext();
	const [email, setEmail] = useState(typeAheadEmail);
	const [isValid, setValid] = useState<boolean>();
	const [isDesktop, setIsDesktop] = useState(() =>
		typeof window !== 'undefined' ? window.matchMedia(DESKTOP_MQ).matches : false,
	);

	const errorMessageText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.invalidEmail',
		defaultMessage: 'Please enter a valid email address.',
	});

	const labelText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.enterWorkEmail',
		defaultMessage: 'Enter work email',
	});

	useEffect(() => {
		const mediaQuery = window.matchMedia(DESKTOP_MQ);
		const onChange = () => setIsDesktop(mediaQuery.matches);
		onChange();
		mediaQuery.addEventListener('change', onChange);
		return () => mediaQuery.removeEventListener('change', onChange);
	}, []);

	useEffect(() => {
		if (!email.trim()) {
			setValid(undefined);
			setIsEmailValid(false);
			return;
		}
		const valid = emailIsValid(email);
		if (!valid && !isReadyForInvalid) {
			setValid(undefined);
			return;
		}
		setValid(valid);
		setIsEmailValid(valid);
	}, [email, setIsEmailValid, isReadyForInvalid]);

	const fields: FormFieldsProps = {
		email,
		setEmail,
		isValid,
		errorMessageText,
	};

	return (
		<div css={containerCss} data-testid="inline-input-submit-social-v2">
			<label css={srOnlyCss} htmlFor="inline_email_embedded_signup_input">
				{labelText}
			</label>
			{isDesktop ? <DesktopForm {...fields} /> : <StackedForm {...fields} />}
			{isValid === false ? (
				<div css={errorCss} role="alert">
					{errorMessageText}
				</div>
			) : null}
		</div>
	);
};

const EmailField = ({ email, setEmail, isValid, errorMessageText }: FormFieldsProps) => (
	<InlineInputV2
		email={email}
		setEmail={setEmail}
		isValid={isValid}
		errorMessageText={errorMessageText}
	/>
);

const DesktopForm = (props: FormFieldsProps) => (
	<div css={desktopLayoutCss}>
		<div
			css={[
				desktopFieldCss,
				props.isValid === undefined && desktopFieldFocusCss,
				props.isValid === false && desktopFieldInvalidCss,
				props.isValid === true && desktopFieldValidCss,
			]}
		>
			<div css={[fieldInputChromeCss, desktopInputCss]}>
				<EmailField {...props} />
			</div>
			<div css={[submitButtonCss, desktopSubmitCss]}>
				<InlineSubmit />
			</div>
		</div>
		<div css={[separatorCss, desktopSeparatorCss]}>
			<InlineSeparator />
		</div>
		<div css={[socialButtonCss, desktopSocialCss]}>
			<SocialButton />
		</div>
	</div>
);

const StackedForm = (props: FormFieldsProps) => (
	<div css={stackedLayoutCss}>
		<div
			css={[
				stackedFieldCss,
				props.isValid === undefined && stackedFieldFocusCss,
				props.isValid === false && stackedFieldInvalidCss,
				props.isValid === true && stackedFieldValidCss,
			]}
		>
			<div css={[fieldInputChromeCss, stackedInputCss]}>
				<EmailField {...props} />
			</div>
		</div>
		<div css={[submitButtonCss, stackedSubmitCss]}>
			<InlineSubmit />
		</div>
		<div css={[separatorCss, stackedSeparatorCss]}>
			<InlineSeparator />
		</div>
		<div css={[socialButtonCss, stackedSocialCss]}>
			<SocialButton />
		</div>
	</div>
);

const containerCss = css({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
});

const srOnlyCss = css({
	border: 0,
	clip: 'rect(0 0 0 0)',
	height: '1px',
	margin: '-1px',
	overflow: 'hidden',
	padding: 0,
	position: 'absolute',
	width: '1px',
	whiteSpace: 'nowrap',
});

/** Desktop: grey outer pill, horizontal — 65px total */
const desktopLayoutCss = css({
	width: '100%',
	height: '65px',
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'nowrap',
	alignItems: 'center',
	gap: '10px',
	backgroundColor: wacToken('color.neutral.light200'),
	borderRadius: 100,
	boxSizing: 'border-box',
	padding: token('space.100'),
	maxWidth: '905px',
	minWidth: 0,
});

/** Tablet/mobile: stacked, no outer chrome (Figma tablet form 490px) */
const stackedLayoutCss = css({
	width: '100%',
	maxWidth: '490px',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	gap: token('space.100'),
});

const desktopFieldCss = css({
	display: 'flex',
	flex: '1 1 485px',
	flexDirection: 'row',
	flexWrap: 'nowrap',
	alignItems: 'center',
	gap: token('space.100'),
	minWidth: 0,
	height: '49px',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light400')}`,
	borderRadius: 50,
	boxSizing: 'border-box',
	paddingBlock: '4px',
	paddingInlineEnd: '4px',
	paddingInlineStart: token('space.200'),
});

const stackedFieldCss = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	width: '100%',
});

const desktopFieldFocusCss = css({
	['&:focus-within']: {
		boxShadow: `inset 0 0 0 1px ${wacToken('color.extended.blue600')}`,
		borderColor: wacToken('color.extended.blue600'),
	},
});

const stackedFieldFocusCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div:first-of-type:focus-within']: {
		borderColor: wacToken('color.extended.blue600'),
		boxShadow: `inset 0 0 0 1px ${wacToken('color.extended.blue600')}`,
	},
});

const desktopFieldInvalidCss = css({
	borderColor: token('color.border.danger'),
	boxShadow: `inset 0 0 0 1px ${token('color.border.danger')}`,
});

const stackedFieldInvalidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div:first-of-type']: {
		borderColor: token('color.border.danger'),
		boxShadow: `inset 0 0 0 1px ${token('color.border.danger')}`,
	},
});

const desktopFieldValidCss = css({
	borderColor: wacToken('color.extended.lime600'),
	boxShadow: `inset 0 0 0 1px ${wacToken('color.extended.lime600')}`,
});

const stackedFieldValidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div:first-of-type']: {
		borderColor: wacToken('color.extended.lime600'),
		boxShadow: `inset 0 0 0 1px ${wacToken('color.extended.lime600')}`,
	},
});

/** Shared input chrome (desktop + stacked) */
const fieldInputChromeCss = css({
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div']: {
		backgroundColor: 'transparent !important',
		border: 'none !important',
		borderRadius: '0 !important',
		boxShadow: 'none !important',
		height: '100% !important',
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input']: {
		fontSize: '16px !important',
		fontWeight: 400,
		letterSpacing: '-0.16px',
		paddingInlineStart: '0 !important',
		paddingInlineEnd: '0 !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input::placeholder']: {
		color: `${wacToken('color.primary.black')} !important`,
		fontSize: '16px !important',
	},
});

const desktopInputCss = css({
	flex: '1 1 auto',
	height: '100%',
	minWidth: 0,
	backgroundColor: 'transparent',
	border: 'none',
	borderRadius: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div']: {
		minWidth: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input']: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

const stackedInputCss = css({
	width: '100%',
	height: '40px',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light400')}`,
	borderRadius: 50,
	paddingInlineStart: token('space.200'),
	paddingInlineEnd: token('space.200'),
});

/** Shared Sign up button styles */
const submitButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		borderRadius: '40px !important',
		fontSize: '16px !important',
		fontWeight: '600 !important',
		height: '41px !important',
		minHeight: '41px !important',
		letterSpacing: '-0.16px',
		margin: '0 !important',
		paddingBlock: '8px !important',
		paddingInline: '24px !important',
		whiteSpace: 'nowrap',
	},
});

const desktopSubmitCss = css({
	width: 'auto',
	flexShrink: 0,
	marginInlineStart: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		maxWidth: 'none !important',
		minWidth: 'auto !important',
		width: 'auto !important',
	},
});

const stackedSubmitCss = css({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		maxWidth: '100% !important',
		width: '100% !important',
	},
});

/** Shared "or" separator text */
const separatorCss = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['span']: {
		fontSize: '16px',
		letterSpacing: '-0.16px',
		lineHeight: 1.56,
	},
});

const desktopSeparatorCss = css({
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['span']: {
		width: '24px',
		textAlign: 'center',
	},
});

const stackedSeparatorCss = css({
	height: '24px',
});

/** Shared Google button styles */
const socialButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		border: `1px solid ${wacToken('color.neutral.light400')} !important`,
		borderRadius: '40px !important',
		color: `${wacToken('color.primary.black')} !important`,
		fontSize: '16px !important',
		fontWeight: '400 !important',
		gap: `${token('space.100')} !important`,
		letterSpacing: '-0.16px',
		margin: '0 !important',
		maxWidth: 'none !important',
		paddingBlock: '11px !important',
		paddingInline: '24px !important',
		width: 'auto !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button:hover']: {
		backgroundColor: `${wacToken('color.neutral.light200')} !important`,
	},
	// Figma Accessibility focus: solid 2px color/focus
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button:focus, button:focus-visible']: {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		borderColor: `${token('color.border.focused')} !important`,
		boxShadow: `inset 0 0 0 1px ${token('color.border.focused')} !important`,
		outline: 'none !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['svg']: {
		height: '20px',
		minHeight: '20px',
		minWidth: '20px',
		width: '20px',
	},
});

const desktopSocialCss = css({
	width: 'auto',
	alignSelf: 'stretch',
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		height: '100% !important',
		minHeight: '49px !important',
	},
});

const stackedSocialCss = css({
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		height: '41px !important',
		minHeight: '41px !important',
	},
});

const errorCss = css({
	color: token('color.text.danger'),
	fontSize: fontSize.xs,
	lineHeight: 1.5,
	marginInlineStart: '24px',
	marginInlineEnd: '24px',
});
